import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './BookExpert.css';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ExpertCard from '../../components/ExpertCard';
import { getExpertImage } from '../../utils/expertImages';
import { authService, therapistService } from '../../services';

const BookExpert = () => {
  const navigate = useNavigate();
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [favoriteExperts, setFavoriteExperts] = useState(new Set());
  const [currentExpertPage, setCurrentExpertPage] = useState(0);
  const expertsPerPage = 4;
  const [isExpertPaused, setIsExpertPaused] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // BookExpert specific form state - unique naming to avoid conflicts
  const [bookExpertFormData, setBookExpertFormData] = useState({
    preferredDate: '',
    preferredTime: '',
    sessionType: '',
    specialRequests: ''
  });
  const [bookExpertValidationErrors, setBookExpertValidationErrors] = useState({});
  const [isBookExpertFormLoading, setIsBookExpertFormLoading] = useState(false);

  // Experts loaded from API
  const [experts, setExperts] = useState([]);
  const [debugExperts, setDebugExperts] = useState({ count: 0, preview: [] });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await therapistService.getAll();
        const raw = Array.isArray(res) ? res : res?.data || [];
        // helper extractors for name and years to support many backend shapes
        const extractName = (r) => {
          if (!r) return '';
          const candidates = [r.display_name, r.displayName, r.DisplayName, r.name, r.fullName, r.userName, r.title];
          for (const c of candidates) if (c) return String(c);
          const nested = r.user || r.profile || r.Person || r.person;
          if (nested) {
            const nc = nested.display_name || nested.displayName || nested.full_name || nested.fullName || nested.name;
            if (nc) return String(nc);
            if (nested.first_name || nested.last_name) return `${nested.first_name || ''} ${nested.last_name || ''}`.trim();
          }
          if ((r.first_name || r.firstName) || (r.last_name || r.lastName)) return `${r.first_name || r.firstName || ''} ${r.last_name || r.lastName || ''}`.trim();
          return '';
        };

        const extractYears = (r) => {
          if (!r) return null;
          const candidates = [r.years_of_experience, r.YearsOfExperience, r.yearsOfExperience, r.experienceYears, r.experience, r.years, r.exp];
          for (const c of candidates) {
            if (c === 0) return 0;
            if (c) {
              if (typeof c === 'number') return c;
              const m = String(c).match(/(\d+(?:[.,]\d+)?)/);
              if (m) return Number(m[1].replace(',', '.'));
              return String(c);
            }
          }
          const nested = r.profile || r.user || r.cv;
          if (nested) {
            const v = nested.years_of_experience || nested.yearsOfExperience || nested.experience;
            if (v === 0) return 0;
            if (v) {
              if (typeof v === 'number') return v;
              const m = String(v).match(/(\d+(?:[\.,]\d+)?)/);
              if (m) return Number(m[1].replace(',', '.'));
              return String(v);
            }
          }
          return null;
        };

        const normalized = raw.map(r => {
          const specialties = Array.isArray(r.specialization) ? r.specialization : (r.specializations || r.specialty ? String(r.specialization || r.specializations || r.specialty).split(',').map(s=>s.trim()) : []);
          const yearsRaw = extractYears(r);
          const experienceStr = (typeof yearsRaw === 'number') ? `${yearsRaw}` : (typeof yearsRaw === 'string' && yearsRaw.length ? yearsRaw : null);
          const qualifications = Array.isArray(r.qualifications) ? r.qualifications : (r.qualifications || r.certifications || r.degrees || r.credentials ? String(r.qualifications || r.certifications || r.degrees || r.credentials).split(',').map(s=>s.trim()) : []);
          return {
            id: r.id || r._id || null,
            name: extractName(r) || '',
            specialty: (specialties[0]) || r.specialty || '',
            specialties,
            experience: experienceStr,
            rating: r.rating ?? r.Rating ?? null,
            price: r.price || r.hourly_rate || null,
            // Prefer explicit avatar fields coming from various backend shapes
            image: r.image || r.avatarUrl || r.avatar || r.avatar_url || (r.user && (r.user.avatarUrl || r.user.avatar_url)) || (r.profile && (r.profile.avatarUrl || r.profile.avatar_url)) || null,
            description: r.description || r.bio || '',
             // Ensure the ternary uses the combined condition rather than relying on operator precedence
             availability: (r.availability || r.isAcceptingClients) ? 'Có lịch' : 'Không rõ',
            qualifications,
            raw: r
          };
        });
        if (mounted) {
          setExperts(normalized);
          setDebugExperts({ count: normalized.length, preview: normalized.slice(0,3) });
        }
      } catch (err) {
        console.error('Failed to load experts for BookExpert', err);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const testimonials = [
    {
      id: 1,
      name: "Mai L.",
      location: "Ho Chi Minh City",
      content: "Tôi đã tìm thấy một nhà trị liệu thực sự hiểu những khó khăn của tôi. Nền tảng này giúp tôi dễ dàng kết nối với ai đó có thể giúp tôi vượt qua lo âu.",
      photo: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=80&fit=crop&crop=face"
    },
    {
      id: 2,
      name: "An N.",
      location: "Hanoi",
      content: "Ban đầu tôi do dự về trị liệu trực tuyến, nhưng nền tảng này làm cho nó trở nên dễ tiếp cận và thoải mái. Đây là một bước ngoặt cho tôi.",
      photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=80&fit=crop&crop=face"
    },
    {
      id: 3,
      name: "Quang D.",
      location: "Da Nang",
      content: "Khả năng lọc theo ngôn ngữ và chuyên môn giúp tôi tìm thấy nhà trị liệu hoàn hảo cho lo âu của tôi. Rất khuyến nghị!",
      photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=80&fit=crop&crop=face"
    },
    {
      id: 4,
      name: "Linh T.",
      location: "Can Tho",
      content: "Quy trình đặt lịch rất mượt mà và nhà trị liệu tôi tìm thấy hoàn hảo cho nhu cầu của tôi. Cảm ơn bạn vì nền tảng tuyệt vời này!",
      photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=60&h=80&fit=crop&crop=face"
    },
    {
      id: 5,
      name: "Minh H.",
      location: "Hai Phong",
      content: "Các nhà trị liệu chuyên nghiệp và hỗ trợ khách hàng xuất sắc. Tôi đã sử dụng dịch vụ này trong 6 tháng và không thể hạnh phúc hơn.",
      photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=80&fit=crop&crop=face"
    },
    {
      id: 6,
      name: "Thu P.",
      location: "Nha Trang",
      content: "Các buổi học video hoạt động hoàn hảo và các nhà trị liệu rất thấu hiểu. Rất khuyến nghị cho bất kỳ ai tìm kiếm hỗ trợ sức khỏe tinh thần.",
      photo: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=50&h=50&fit=crop&crop=face"
    }
  ];

  const handleBookExpert = (expert) => {
    setSelectedExpert(expert);
    setShowBookingForm(true);
  };

  const toggleFavorite = (expertId) => {
    setFavoriteExperts(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(expertId)) {
        newFavorites.delete(expertId);
      } else {
        newFavorites.add(expertId);
      }
      return newFavorites;
    });
  };

  const handlePrevExpertPage = () => {
    setCurrentExpertPage(prev => Math.max(0, prev - 1));
  };

  const handleNextExpertPage = () => {
    const maxPage = Math.ceil(experts.length / expertsPerPage) - 1;
    setCurrentExpertPage(prev => Math.min(maxPage, prev + 1));
  };

  const getCurrentExperts = () => {
    const startIndex = currentExpertPage * expertsPerPage;
    const endIndex = startIndex + expertsPerPage;
    // Always return at least empty array to ensure section is visible
    return experts.length > 0 ? experts.slice(startIndex, endIndex) : [];
  };

  const handleBackToExperts = () => {
    setShowBookingForm(false);
    setSelectedExpert(null);
  };

  // BookExpert specific form handlers - unique naming to avoid conflicts
  const handleBookExpertInputChange = (e) => {
    const { name, value } = e.target;
    setBookExpertFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (bookExpertValidationErrors[name]) {
      setBookExpertValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateBookExpertForm = () => {
    const newErrors = {};
    
    if (!bookExpertFormData.preferredDate) {
      newErrors.preferredDate = 'Ngày ưa thích là bắt buộc';
    }
    
    if (!bookExpertFormData.preferredTime) {
      newErrors.preferredTime = 'Thời gian ưa thích là bắt buộc';
    }
    
    if (!bookExpertFormData.sessionType) {
      newErrors.sessionType = 'Loại buổi học là bắt buộc';
    }
    
    setBookExpertValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBookExpertSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateBookExpertForm()) {
      return;
    }
    
    setIsBookExpertFormLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Save expert info to localStorage
      localStorage.setItem('selectedExpert', JSON.stringify(selectedExpert));
      
      // Navigate to booking dashboard with form data and expert info
      navigate('/booking-dashboard', { 
        state: { 
          expert: selectedExpert,
          formData: bookExpertFormData
        }
      });
      
      console.log('BookExpert form submitted:', bookExpertFormData);
      
    } catch (error) {
      console.error('Error submitting BookExpert form:', error);
    } finally {
      setIsBookExpertFormLoading(false);
    }
  };

  const handlePrevTestimonial = () => {
    setCurrentTestimonialIndex((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const handleNextTestimonial = () => {
    setCurrentTestimonialIndex((prev) => 
      prev === testimonials.length - 1 ? 0 : prev + 1
    );
  };

  // Get current testimonials to display (3 at a time)
  const getCurrentTestimonials = () => {
    const start = currentTestimonialIndex;
    const end = start + 3;
    if (end <= testimonials.length) {
      return testimonials.slice(start, end);
    } else {
      return [
        ...testimonials.slice(start),
        ...testimonials.slice(0, end - testimonials.length)
      ];
    }
  };

  // Auto-slide testimonials every 5 seconds
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => 
        prev === testimonials.length - 1 ? 0 : prev + 1
      );
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [testimonials.length, isPaused]);

  // Auto-slide experts
  useEffect(() => {
    if (isExpertPaused) return;

    const interval = setInterval(() => {
      setCurrentExpertPage((prev) => 
        prev === Math.ceil(experts.length / expertsPerPage) - 1 ? 0 : prev + 1
      );
    }, 3000); // 3 seconds

    return () => clearInterval(interval);
  }, [experts.length, expertsPerPage, isExpertPaused]);

  // Load current user on component mount
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
      console.log('Current user loaded:', user);
    } else {
      console.log('No user found, redirecting to login');
      navigate('/login');
    }
  }, [navigate]);

  if (showBookingForm && selectedExpert) {
    return (
      <div className="book-expert-page page-with-header">
        <Header />

        <div className="booking-form-container">
          <div className="booking-form">
            <div className="form-header">
              <button className="back-btn" onClick={handleBackToExperts}>
                ← Quay lại chuyên gia
              </button>
              <h1>Đặt lịch với {selectedExpert.name}</h1>
            </div>

            <div className="expert-info-card">
              <img 
                src={getExpertImage(selectedExpert)} 
                alt={selectedExpert.name} 
                className="expert-photo"
                onError={(e) => {
                  const name = selectedExpert.name || 'Expert';
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6B7280&color=fff&size=100`;
                }}
              />
              <div className="expert-details">
                <h2>{selectedExpert.name}</h2>
                <p className="specialty">{selectedExpert.specialty}</p>
                <p className="experience">{selectedExpert.experience} kinh nghiệm</p>
                <div className="rating">
                  <span className="stars">★★★★★</span>
                  <span className="rating-text">{selectedExpert.rating}/5</span>
                </div>
                <p className="price">{selectedExpert.price}</p>
                <p className="availability">{selectedExpert.availability}</p>
              </div>
              <div className="expert-actions">
                <button className="take-lesson-btn" onClick={() => handleBookExpert(selectedExpert)}>
                  Tham gia bài học
                </button>
              </div>
            </div>

            <form className="booking-details" onSubmit={handleBookExpertSubmit}>
              <div className="form-group">
                <label>Ngày ưa thích</label>
                <input 
                  type="date" 
                  name="preferredDate"
                  value={bookExpertFormData.preferredDate}
                  onChange={handleBookExpertInputChange}
                  required 
                />
                {bookExpertValidationErrors.preferredDate && (
                  <span className="error-message">{bookExpertValidationErrors.preferredDate}</span>
                )}
              </div>

              <div className="form-group">
                <label>Thời gian ưa thích</label>
                <select 
                  name="preferredTime"
                  value={bookExpertFormData.preferredTime}
                  onChange={handleBookExpertInputChange}
                  required
                >
                  <option value="">Chọn thời gian</option>
                  <option value="09:00">9:00 AM</option>
                  <option value="10:00">10:00 AM</option>
                  <option value="11:00">11:00 AM</option>
                  <option value="14:00">2:00 PM</option>
                  <option value="15:00">3:00 PM</option>
                  <option value="16:00">4:00 PM</option>
                </select>
                {bookExpertValidationErrors.preferredTime && (
                  <span className="error-message">{bookExpertValidationErrors.preferredTime}</span>
                )}
              </div>

              <div className="form-group">
                <label>Loại buổi học</label>
                <select 
                  name="sessionType"
                  value={bookExpertFormData.sessionType}
                  onChange={handleBookExpertInputChange}
                  required
                >
                  <option value="">Chọn loại</option>
                  <option value="video">Cuộc gọi video</option>
                  <option value="phone">Cuộc gọi điện thoại</option>
                  <option value="in-person">Gặp trực tiếp</option>
                </select>
                {bookExpertValidationErrors.sessionType && (
                  <span className="error-message">{bookExpertValidationErrors.sessionType}</span>
                )}
              </div>

              <div className="form-group">
                <label>Yêu cầu đặc biệt hoặc ghi chú</label>
                <textarea 
                  name="specialRequests"
                  value={bookExpertFormData.specialRequests}
                  onChange={handleBookExpertInputChange}
                  placeholder="Bất kỳ mối quan tâm cụ thể hoặc sở thích nào cho buổi học này..."
                ></textarea>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={handleBackToExperts}>
                  Hủy
                </button>
                <button 
                  type="submit" 
                  className="book-btn"
                  disabled={isBookExpertFormLoading}
                >
                  {isBookExpertFormLoading ? 'Đang đặt lịch...' : `Đặt lịch - ${selectedExpert.price}`}
                </button>
              </div>
            </form>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="book-expert-page page-with-header">
        <Header />

      <div className="main-content">
        <div className="search-section">
          <div className="search-container">
            <div className="search-bar">
              <svg className="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16ZM19 19l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input type="text" placeholder="Tìm kiếm nhà tâm lý học" className="search-input" />
              <svg className="mic-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 1a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 10v2a9 9 0 0 1-18 0v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="10" y1="19" x2="10" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="7" y1="23" x2="13" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
        </div>

        <div className="page-header">
          <h1>Đặt lịch chuyên gia</h1>
          <p>Chọn từ các chuyên gia sức khỏe tinh thần được chứng nhận của chúng tôi</p>
        </div>

        {/* Why Choose Us Section */}
        <div className="why-choose-section">
          <div className="why-choose-grid">
            <div className="why-choose-item">
              <div className="why-choose-icon">🏥</div>
              <h3>Chuyên gia có giấy phép</h3>
              <p>Tất cả các nhà trị liệu của chúng tôi đều được chứng nhận và có kinh nghiệm về sức khỏe tinh thần</p>
            </div>
            <div className="why-choose-item">
              <div className="why-choose-icon">🌐</div>
              <h3>Hỗ trợ đa ngôn ngữ</h3>
              <p>Có sẵn bằng tiếng Việt và tiếng Anh để giao tiếp tốt hơn</p>
            </div>
            <div className="why-choose-item">
              <div className="why-choose-icon">💻</div>
              <h3>Buổi học linh hoạt</h3>
              <p>Các buổi học trực tuyến và trực tiếp có sẵn để phù hợp với lịch trình của bạn</p>
            </div>
            <div className="why-choose-item">
              <div className="why-choose-icon">🔒</div>
              <h3>Bảo mật & An toàn</h3>
              <p>Quyền riêng tư và bảo mật dữ liệu của bạn là ưu tiên hàng đầu của chúng tôi</p>
            </div>
          </div>
        </div>

        <div 
          className="experts-section"
          onMouseEnter={() => setIsExpertPaused(true)}
          onMouseLeave={() => setIsExpertPaused(false)}
        >
          <div className="experts-header">
            <h2>Các nhà trị liệu chuyên gia của chúng tôi</h2>
            <div className="expert-nav">
              <button 
                className="nav-btn prev-btn"
                onClick={handlePrevExpertPage}
                disabled={currentExpertPage === 0}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button 
                className="nav-btn next-btn"
                onClick={handleNextExpertPage}
                disabled={currentExpertPage >= Math.ceil(experts.length / expertsPerPage) - 1}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="auto-slide-indicator">
                <div className={`indicator-dot ${!isExpertPaused ? 'active' : ''}`}></div>
              </div>
            </div>
          </div>
          <div className="experts-grid">
            {getCurrentExperts().length > 0 ? (
              getCurrentExperts().map((expert) => (
                <ExpertCard
                  key={expert.id}
                  expert={expert}
                  isFavorite={favoriteExperts.has(expert.id)}
                  onToggleFavorite={toggleFavorite}
                  bookHref="/booking-dashboard"
                  bookState={{ expert }}
                  bookLabel="Đặt lịch"
                />
              ))
            ) : (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#64748B' }}>
                <p>Đang tải danh sách chuyên gia...</p>
              </div>
            )}
          </div>
          <div className="expert-dots">
            {Array.from({ length: Math.ceil(experts.length / expertsPerPage) }, (_, index) => (
              <button
                key={index}
                className={`dot ${index === currentExpertPage ? 'active' : ''}`}
                onClick={() => setCurrentExpertPage(index)}
              />
            ))}
          </div>
        </div>

        {/* Statistics Section */}
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">500+</div>
              <div className="stat-label">Buổi học hoàn thành</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">98%</div>
              <div className="stat-label">Sự hài lòng của khách hàng</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Hỗ trợ có sẵn</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">15+</div>
              <div className="stat-label">Nhà trị liệu chuyên gia</div>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div 
          className="testimonials-section"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="testimonials-header">
            <h2>Mọi người nói gì</h2>
            <div className="testimonial-controls">
              <div className="auto-slide-indicator">
                <div className={`indicator-dot ${!isPaused ? 'active' : ''}`}></div>
              </div>
              <div className="testimonial-nav">
                <button className="nav-btn prev-btn" onClick={handlePrevTestimonial}>←</button>
                <button className="nav-btn next-btn" onClick={handleNextTestimonial}>→</button>
              </div>
            </div>
          </div>
          <div className="testimonials-grid">
            {getCurrentTestimonials().map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <div className="testimonial-content">
                  <p>"{testimonial.content}"</p>
                </div>
                <div className="testimonial-author">
                  <img 
                    src={testimonial.photo} 
                    alt={testimonial.name} 
                    className="author-photo"
                    onError={(e) => {
                      e.target.src = `https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face`;
                    }}
                  />
                  <div className="author-info">
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-location">{testimonial.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="testimonial-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentTestimonialIndex ? 'active' : ''}`}
                onClick={() => setCurrentTestimonialIndex(index)}
              />
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookExpert;

